import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { numberID, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GRDetail } from "./PurchaseDetail";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PackageCheck, Truck, ReceiptText } from "lucide-react";

export default function GoodsReceiving() {
  const [detail, setDetail] = useState(null);
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(false);
  const [billablePoIds, setBillablePoIds] = useState(new Set());
  const canReceive = can("purchasing.receive");
  const canBill = can("payables.bill.manage");
  const load = () => {
    setRows(null);
    api.get("/purchasing/goods-receipts").then((r) => setRows(r.data));
    api.get("/purchasing/purchase-orders").then((r) => setPos(r.data.filter((p) => ["Disetujui", "Dikirim ke Supplier", "Sebagian Diterima"].includes(p.status))));
    if (canBill && can("payables.view")) api.get("/payables/billable-pos").then((r) => setBillablePoIds(new Set(r.data.map((p) => p.po_id)))).catch(() => {});
  };
  useEffect(load, []);
  return (
    <div>
      <PageHeader title="Penerimaan Barang" subtitle="Terima barang dari PO (mendukung parsial). Stok masuk ke inventory tujuan otomatis." testId="gr-header">
        {canReceive && <Button onClick={() => setOpen(true)} data-testid="add-gr-btn" className="bg-indigo-600 hover:bg-indigo-500"><PackageCheck className="mr-1.5 h-4 w-4" /> Terima Barang</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada penerimaan" icon={PackageCheck} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No GR</TableHead><TableHead>PO</TableHead><TableHead>Supplier</TableHead><TableHead>Tujuan</TableHead><TableHead>Tanggal</TableHead><TableHead>Penerima</TableHead><TableHead className="text-right">Baris Item</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((gr) => (
                <TableRow key={gr.id} data-testid={`gr-row-${gr.gr_no}`}>
                  <TableCell className="font-mono text-xs">{gr.gr_no}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{gr.po_no}</TableCell>
                  <TableCell>{gr.supplier_name}</TableCell>
                  <TableCell className="text-slate-500">{gr.warehouse_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{gr.tanggal}</TableCell>
                  <TableCell>{gr.penerima}</TableCell>
                  <TableCell className="text-right">{gr.items.reduce((s, i) => s + (i.qty_received || 0), 0)} diterima</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDetail(gr.id)} data-testid={`gr-detail-${gr.gr_no}`}>Detail</Button>
                    {canBill && billablePoIds.has(gr.po_id) && <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-600" onClick={() => nav(`/payables/bills?po=${gr.po_id}&gr=${gr.gr_no}`)} data-testid={`bill-from-gr-${gr.gr_no}`}><ReceiptText className="mr-1 h-3.5 w-3.5" />Buat Tagihan</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <GRDialog pos={pos} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detail && <GRDetail id={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function GRDialog({ pos, onClose, onSaved }) {
  const today = wibToday();
  const [poId, setPoId] = useState("");
  const [f, setF] = useState({ tanggal: today, penerima: "", notes: "", attachment_ref: "" });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cwEnabled, setCwEnabled] = useState(false);
  const idemRef = useRef(null); // one key per submit-intent; reused on retry so double-click/timeout can't create duplicates
  useEffect(() => { api.get("/purchasing/catch-weight/config").then((r) => setCwEnabled(!!r.data.fabric_catch_weight_enabled)).catch(() => setCwEnabled(false)); }, []);
  const po = pos.find((p) => p.id === poId);
  useEffect(() => {
    if (!po) { setLines([]); return; }
    setLines(po.items.map((it) => ({ po_item_id: it.id, item_name: it.item_name, unit: it.unit, remaining: it.qty_ordered - (it.qty_received || 0), qty_received: 0, qty_rejected: 0, batch: "", tracking_mode: cwEnabled ? (it.tracking_mode || "") : "", estimated_weight_kg: it.estimated_weight_kg || 0, unit_price_per_kg: it.unit_price_per_kg || 0, rolls: [] })));
  }, [poId, cwEnabled]);
  const save = async () => {
    if (!po) return toast.error("Pilih PO");
    const items = [];
    for (const l of lines) {
      if (l.tracking_mode === "CATCH_WEIGHT_ROLL") {
        const rolls = (l.rolls || []).filter((r) => r.roll_no && Number(r.weight_kg) > 0);
        if (rolls.length === 0) continue;
        if (rolls.length > l.remaining) return toast.error(`Jumlah roll melebihi sisa PO untuk ${l.item_name}`);
        items.push({ po_item_id: l.po_item_id, rolls: rolls.map((r) => ({ roll_no: r.roll_no, weight_kg: Number(r.weight_kg) })) });
      } else if (Number(l.qty_received) > 0 || Number(l.qty_rejected) > 0) {
        if (Number(l.qty_received) > l.remaining) return toast.error(`Qty diterima melebihi sisa PO untuk ${l.item_name}`);
        items.push({ po_item_id: l.po_item_id, qty_received: Number(l.qty_received), qty_rejected: Number(l.qty_rejected), batch: l.batch });
      }
    }
    if (items.length === 0) return toast.error("Isi minimal 1 qty diterima");
    if (!idemRef.current) idemRef.current = (window.crypto?.randomUUID?.() || `gr-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setSaving(true);
    try {
      await api.post("/purchasing/goods-receipts", { po_id: poId, ...f, items, idempotency_key: idemRef.current });
      toast.success("Barang diterima, stok diperbarui"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Terima Barang</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs text-slate-500">Purchase Order</Label><Select value={poId} onValueChange={setPoId}><SelectTrigger className="mt-1" data-testid="gr-po"><SelectValue placeholder="Pilih PO..." /></SelectTrigger><SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_no} · {p.supplier_name} → {p.warehouse_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Tanggal Diterima</Label><Input type="date" className="mt-1" value={f.tanggal} onChange={(e) => setF({ ...f, tanggal: e.target.value })} data-testid="gr-tanggal" /></div>
          <div><Label className="text-xs text-slate-500">Penerima</Label><Input className="mt-1" value={f.penerima} onChange={(e) => setF({ ...f, penerima: e.target.value })} /></div>
        </div>
        {po && (
          <div className="rounded-lg border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Sisa PO</TableHead><TableHead className="text-right">Qty Diterima</TableHead><TableHead className="text-right">Qty Ditolak</TableHead><TableHead>Batch</TableHead></TableRow></TableHeader>
              <TableBody>
                {lines.map((l, i) => {
                  if (l.tracking_mode === "CATCH_WEIGHT_ROLL") {
                    const rolls = l.rolls || [];
                    const sumKg = rolls.reduce((s, r) => s + (Number(r.weight_kg) || 0), 0);
                    const variance = Math.round((sumKg - (Number(l.estimated_weight_kg) || 0)) * 1000) / 1000;
                    const setRolls = (nr) => setLines(lines.map((x, idx) => idx === i ? { ...x, rolls: nr } : x));
                    return (
                      <TableRow key={l.po_item_id}>
                        <TableCell colSpan={5}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{l.item_name} <span className="text-xs text-indigo-600">(catch-weight · sisa {numberID(l.remaining)} roll · Rp{numberID(l.unit_price_per_kg)}/KG)</span></span>
                            <Button size="sm" variant="outline" data-testid={`gr-addroll-${i}`} onClick={() => setRolls([...rolls, { roll_no: "", weight_kg: "" }])}>+ Roll</Button>
                          </div>
                          <div className="mt-2 space-y-1">
                            {rolls.map((r, ri) => (
                              <div key={ri} className="flex items-center gap-2">
                                <span className="w-14 text-xs text-slate-400">Roll {ri + 1}</span>
                                <Input className="h-8 w-40" placeholder="No. Roll" value={r.roll_no} onChange={(e) => setRolls(rolls.map((x, j) => j === ri ? { ...x, roll_no: e.target.value } : x))} data-testid={`gr-rollno-${i}-${ri}`} />
                                <Input type="number" min="0" step="0.01" className="h-8 w-28" placeholder="KG" value={r.weight_kg} onChange={(e) => setRolls(rolls.map((x, j) => j === ri ? { ...x, weight_kg: e.target.value } : x))} data-testid={`gr-rollkg-${i}-${ri}`} />
                                <Button variant="ghost" size="sm" className="text-rose-500" onClick={() => setRolls(rolls.filter((_, j) => j !== ri))} data-testid={`gr-rollrm-${i}-${ri}`}>Hapus</Button>
                              </div>
                            ))}
                            {rolls.length === 0 && <p className="text-xs text-slate-400">Klik “+ Roll” untuk input berat aktual per roll.</p>}
                          </div>
                          <div className="mt-2 text-xs text-slate-600" data-testid={`gr-rollsummary-${i}`}>
                            Total Roll: <b>{rolls.length}</b> · Total Berat: <b>{numberID(Math.round(sumKg * 1000) / 1000)} KG</b> · Estimasi PO: {numberID(l.estimated_weight_kg)} KG · Variance: <b className={variance >= 0 ? "text-emerald-600" : "text-amber-600"}>{variance >= 0 ? "+" : ""}{numberID(variance)} KG</b> · Nilai: <b>Rp{numberID(Math.round(sumKg * (Number(l.unit_price_per_kg) || 0) * 100) / 100)}</b>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return (
                  <TableRow key={l.po_item_id}>
                    <TableCell className="font-medium">{l.item_name} <span className="text-xs text-slate-400">({l.unit})</span></TableCell>
                    <TableCell className="text-right text-slate-500">{numberID(l.remaining)}</TableCell>
                    <TableCell className="text-right"><Input type="number" min="0" max={l.remaining} className="h-8 w-20 ml-auto" value={l.qty_received} onChange={(e) => setLines(lines.map((x, idx) => idx === i ? { ...x, qty_received: e.target.value } : x))} data-testid={`gr-recv-${i}`} /></TableCell>
                    <TableCell className="text-right"><Input type="number" min="0" className="h-8 w-20 ml-auto" value={l.qty_rejected} onChange={(e) => setLines(lines.map((x, idx) => idx === i ? { ...x, qty_rejected: e.target.value } : x))} /></TableCell>
                    <TableCell><Input className="h-8 w-24" value={l.batch} onChange={(e) => setLines(lines.map((x, idx) => idx === i ? { ...x, batch: e.target.value } : x))} /></TableCell>
                  </TableRow>
                ); })}
              </TableBody>
            </Table>
          </div>
        )}
        <div><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        <p className="text-[11px] text-slate-400">Lampiran foto/surat jalan akan didukung pada fase berikutnya (hook tersedia).</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-gr-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Konfirmasi Penerimaan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
